<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import Multiselect from "vue-multiselect";
import VueDropify from "vue-dropify";

import UserService from "@/helpers/APIService/user.service";
import { authComputed } from "@/state/helpers";
import errorHandler from "@/helpers/errorHandler";

export default {
  data() {
    return {
      plugins: [
        "advlist autolink link image lists charmap print preview hr anchor pagebreak spellchecker",
        "searchreplace wordcount visualblocks visualchars code fullscreen insertdatetime media nonbreaking",
        "save table contextmenu directionality emoticons template paste textcolor",
      ],
      toolbar:
        "insertfile undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | l      ink image | print preview media fullpage | forecolor backcolor emoticons",
      options: {
        height: 300,
        style_formats: [
          { title: "Bold text", inline: "b" },
          { title: "Red text", inline: "span", styles: { color: "#ff0000" } },
          { title: "Red header", block: "h1", styles: { color: "#ff0000" } },
          { title: "Example 1", inline: "span", classes: "example1" },
          { title: "Example 2", inline: "span", classes: "example2" },
          { title: "Table styles" },
          { title: "Table row 1", selector: "tr", classes: "tablerow1" },
        ],
      },
      tvChannel: {
        name: null,
        channel_number: null,
        description: null,
        category: null,
        is_active: true,
        image: null,
      },
      imgURL: null,
      submitted: false,
      isBtnLoading: false,
      isSaveAndAddNew: false,
      categoryOption: [],
    };
  },
  components: {
    Multiselect,
    VueDropify,
  },
  props: {
    currentProps: Object,
  },
  emits: ["submitted", "cancelSubmit"],
  computed: {
    ...authComputed,
  },
  validations: {
    tvChannel: {
      name: {
        required,
      },
      category: {
        required,
      },
      image: {
        required: requiredIf(function () {
          if (this.imgURL === null && this.tvChannel.image == null) {
            return true;
          }
        }),
      },
    },
  },
  mounted() {
    this.getTVChannelCategoryList();
    this.setIfEdit();
  },
  methods: {
    getTVChannelCategoryList() {
      UserService.getTvChannelCategory().then((response) => {
        response.map((item) => {
          this.categoryOption.push({
            id: item.id,
            name: item.name,
            slug_name: item.slug_name,
          });
        });
      });
    },
    // Method is use for maintained edit data display
    setIfEdit() {
      if (this.currentProps.isEdit) {
        this.tvChannel.category =
          this.currentProps.selectedTVChannel.tv_channel_category;
        this.tvChannel.image = this.currentProps.selectedTVChannel.image;
        setTimeout(() => {
          this.tvChannel.description =
            this.currentProps.selectedTVChannel.description;
        }, 100);
        this.tvChannel.name = this.currentProps.selectedTVChannel.name;
        this.tvChannel.channel_number =
          this.currentProps.selectedTVChannel.channel_number;
      }
    },
    // Method is for maintained submit event
    onSubmit() {
      this.submitted = true;
      this.isBtnLoading = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.isBtnLoading = false;
        return;
      } else {
        let data = {
          hotel: this.hotel.id,
          name: this.tvChannel.name,
          channel_number: this.tvChannel.channel_number,
          tv_channel_category: this.tvChannel.category.id,
          description: this.tvChannel.description,
        };
        delete this.tvChannel.image;
        let formData = new FormData();
        if (this.imgURL !== null) {
          formData.append("image", this.imgURL);
        }
        Object.keys(data).map((key) => {
          if (data[key] !== null) {
            formData.append(key, data[key]);
          }
        });
        if (this.currentProps.isEdit == false) {
          formData.append("is_active", true);
          UserService.addTVChannel(formData)
            .then((tvChannel) => {
              if (this.isSaveAndAddNew) {
                this.$emit("saveAndAddNew", tvChannel);
                this.tvChannel = {
                  name: "",
                  channel_number: "",
                  description: "",
                  category: "",
                };
              } else {
                this.$emit("submitted", tvChannel);
              }
              this.isBtnLoading = false;
              this.submitted = false;
            })
            .catch((error) => {
              this.isBtnLoading = false;
              errorHandler(error);
            });
        } else {
          UserService.updateTVChannel(
            this.currentProps.selectedTVChannel.id,
            formData
          )
            .then((tvChannel) => {
              this.$emit("submitted", tvChannel);
              this.isBtnLoading = false;
              this.submitted = false;
            })
            .catch((error) => {
              this.isBtnLoading = false;
              errorHandler(error);
            });
        }
      }
    },
    // Method is use for save and add new tvChannel continue
    saveAndAddNew() {
      this.isSaveAndAddNew = true;
      this.onSubmit();
    },
    // Method is use for cancel to add new tvChannel
    cancelSubmit() {
      this.$emit("cancelSubmit");
      this.isSaveAndAddNew = false;
    },
    // Method is use for maintained change image event
    changeNearByPlaceImage(event) {
      this.imgURL = event[0] == undefined ? null : event[0];
    },
  },
};
</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-12">
        <h4>
          {{ this.currentProps.isEdit ? "Update" : "Add New" }} TV Channel
        </h4>
        <hr />
      </div>
      <div class="col-md-6">
        <b-form-group
          class="mb-3 form-label"
          label="Category"
          label-for="category"
        >
          <template slot="label">
            Category <span class="text-danger">*</span>
          </template>
          <multiselect
            v-model="tvChannel.category"
            :options="categoryOption"
            :show-labels="false"
            track-by="id"
            label="name"
            placeholder="Select category"
            :class="{
              'is-invalid': submitted && $v.tvChannel.category.$error,
            }"
          ></multiselect>

          <div
            v-if="submitted && $v.tvChannel.category.$error"
            class="invalid-feedback"
          >
            <span v-if="!$v.tvChannel.category.required"
              >Category is required.</span
            >
          </div>
        </b-form-group>
      </div>
      <div class="col-md-3">
        <b-form-group class="mb-3" label="Name" label-for="tvChannel_name">
          <template slot="label">
            Name <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="tvChannel_name"
            type="text"
            autocomplete="off"
            placeholder="Ex : Channel Name"
            v-model="tvChannel.name"
            :class="{
              'is-invalid': submitted && $v.tvChannel.name.$error,
            }"
          ></b-form-input>
          <div v-if="submitted" class="invalid-feedback">
            <span v-if="!$v.tvChannel.name.required">Name is required.</span>
          </div>
        </b-form-group>
      </div>
      <div class="col-md-3">
        <b-form-group class="mb-3" label="Name" label-for="tvChannel_number">
          <template slot="label">Number </template>
          <b-form-input
            id="tvChannel_number"
            type="text"
            autocomplete="off"
            placeholder="Ex : 101"
            v-model="tvChannel.channel_number"
          ></b-form-input>
        </b-form-group>
      </div>
      <div class="col-md-12">
        <!-- description -->
        <div class="mb-3">
          <label for="description">
            <span slot="label"
              >Description <span class="text-danger">*</span></span
            >
          </label>

          <tinymce
            id="tinyMce-editor"
            v-model="tvChannel.description"
            :plugins="plugins"
            :toolbar="toolbar"
            :other_options="options"
          ></tinymce>
        </div>

        <!-- upload img -->
        <div class="mb-3">
          <label for="Image">
            <span slot="label">Image <span class="text-danger">*</span></span>
          </label>
          <VueDropify
            @upload="changeNearByPlaceImage($event)"
            :src="tvChannel.image"
            message="Drop image or click here to select"
            id="tv_channel_images"
            :class="{
              'is-invalid': submitted && $v.tvChannel.image.$error,
            }"
          />
          <div v-if="submitted" class="invalid-feedback">
            <span v-if="!$v.tvChannel.image.required">Image is required.</span>
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <b-button
          v-if="$route.path == '/tv-channel'"
          class="float-end ms-2"
          variant="outline-primary"
          type="reset"
          @click="cancelSubmit"
          >Cancel
        </b-button>
        <b-button
          v-if="this.currentProps.isEdit === false"
          class="float-end ms-2"
          variant="outline-primary"
          type="submit"
          @click="saveAndAddNew"
          :class="{
            disabled: isSaveAndAddNew === true && isBtnLoading,
          }"
        >
          <i
            v-if="isSaveAndAddNew === true && isBtnLoading"
            class="bx bx-loader bx-spin font-size-16 align-middle me-2"
          ></i
          >Save & Add
        </b-button>
        <b-button
          class="float-end"
          variant="outline-primary"
          type="submit"
          @click="onSubmit"
          :class="{
            disabled: isSaveAndAddNew === false && isBtnLoading,
          }"
        >
          <i
            class="bx bx-loader bx-spin font-size-16 align-middle me-2"
            v-if="isSaveAndAddNew === false && isBtnLoading"
          ></i>
          {{ this.currentProps.isEdit ? "Update" : "Save" }}
        </b-button>
      </div>
    </div>
  </div>
</template>
